import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useMemo, SyntheticEvent } from "react";

import { normalizeErrorMessages } from "api/common/utils/rtkq.utils";
import { useGetFeaturesQuery } from "api/memberPortal/features/features/features.api";
import { useCreateProductFeatureMutation } from "api/memberPortal/features/productFeatures/productFeatures.api";
import { ProductFeature } from "api/memberPortal/features/productFeatures/productFeatures.types";

import TestedFeatureListItem from "./TestedFeatureListItem";
import TestedFeatureReadOnlyItem from "./TestedFeatureReadOnlyItem";

import type { Feature } from "api/memberPortal/features/features/features.types";

interface TestedFeatureListProps {
  featureType: "product" | "pcm";
  featureId: number;
  features: ProductFeature[];
  readOnly: boolean;
}

const TestedFeatureList = ({
  featureType,
  featureId,
  features,
  readOnly,
}: TestedFeatureListProps) => {
  const [featureName, setFeatureName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);

  const [createProductFeature, { isLoading }] =
    useCreateProductFeatureMutation();
  const { data: allFeatures = [] } = useGetFeaturesQuery({});

  // Memoize filtered search results to exclude already added features and filter by input
  const availableFeatures = useMemo(() => {
    const existingFeatureNames = new Set(
      features.map((f) => f.feature_name.toLowerCase()),
    );
    const searchTerm = featureName.toLowerCase().trim();

    return allFeatures.filter((feature) => {
      const isNotAdded = !existingFeatureNames.has(feature.name.toLowerCase());
      const matchesSearch =
        !searchTerm || feature.name.toLowerCase().includes(searchTerm);
      return isNotAdded && matchesSearch;
    });
  }, [allFeatures, features, featureName]);

  const handleAddFeature = async () => {
    if (!featureName.trim()) return;

    setError(null);

    // Check if feature already exists in this product/pcm (case-insensitive)
    const normalizedInput = featureName.trim().toLowerCase();
    const isDuplicateInProduct = features.some(
      (f) => f.feature_name.toLowerCase() === normalizedInput,
    );

    if (isDuplicateInProduct) {
      setError("This feature already exists for this item.");
      return;
    }

    // Check if a similar feature exists in the global feature list (case-insensitive)
    // If it does, use the existing feature's exact name for consistency
    const existingFeature = allFeatures.find(
      (f) => f.name.toLowerCase() === normalizedInput,
    );
    const featureNameToUse = existingFeature
      ? existingFeature.name
      : featureName.trim();

    try {
      await createProductFeature({
        feature_name: featureNameToUse,
        ...(featureType === "product"
          ? { product: featureId }
          : { pcm: featureId }),
      }).unwrap();

      setFeatureName("");
    } catch (err: any) {
      let errorMessage = normalizeErrorMessages(err);

      // Check for duplicate key error
      if (errorMessage.toLowerCase().includes("duplicate key value")) {
        errorMessage = "This feature already exists for this item.";
      }

      setError(errorMessage);
    }
  };

  const handleInputChange = (_event: SyntheticEvent, value: string) => {
    setFeatureName(value);
  };

  const handleSelectionChange = (
    _event: SyntheticEvent,
    value: Feature | string | null,
  ) => {
    if (typeof value === "string") {
      // User typed a custom value
      setFeatureName(value);
    } else if (value) {
      // User selected an existing feature
      setFeatureName(value.name);
    } else {
      // Clear selection
      setFeatureName("");
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 2,
        minHeight: 250,
        p: 2,
        backgroundColor: "grey.50",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />
        <Typography variant="h6">Tested Features</Typography>
        <Chip
          label={features.length}
          size="small"
          sx={{
            backgroundColor: "grey.700",
            color: "white",
            fontWeight: 600,
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={1} sx={{ mb: 2, flexGrow: 1 }}>
        {features.length === 0 ? (
          <Typography variant="body2">No tested features yet</Typography>
        ) : (
          features.map((feature) =>
            readOnly ? (
              <TestedFeatureReadOnlyItem key={feature.id} feature={feature} />
            ) : (
              <TestedFeatureListItem
                key={feature.id}
                feature={feature}
                featureId={featureId}
                featureType={featureType}
                onError={setError}
              />
            ),
          )
        )}
      </Stack>
      {!readOnly && (
        <Box sx={{ mt: "auto", display: "flex", gap: 1, alignItems: "center" }}>
          <Autocomplete
            fullWidth
            freeSolo
            options={availableFeatures}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.name
            }
            inputValue={featureName}
            onInputChange={handleInputChange}
            onChange={handleSelectionChange}
            onOpen={() => setIsAutocompleteOpen(true)}
            onClose={() => setIsAutocompleteOpen(false)}
            disabled={isLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Enter feature name..."
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    featureName.trim() &&
                    !isLoading &&
                    !isAutocompleteOpen
                  ) {
                    e.preventDefault();
                    handleAddFeature();
                  }
                }}
              />
            )}
          />
          <IconButton
            onClick={handleAddFeature}
            disabled={!featureName.trim() || isLoading}
            sx={{
              color: "success.main",
              border: "1px solid",
              borderColor: "grey.400",
              "&:hover": {
                borderColor: "success.main",
                backgroundColor: "success.50",
              },
              "&:disabled": {
                borderColor: "grey.300",
              },
            }}
          >
            <AddIcon />
          </IconButton>
        </Box>
      )}
    </Paper>
  );
};

export default TestedFeatureList;
